import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Clock, Package, Calculator, Phone, ChevronRight, ArrowLeft, Building, Home } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import bulkDeliveryHero from '@/assets/bulk-delivery-hero.jpg';

const BulkDeliveryPage = () => {
  const { language, t } = useLanguage();

  const seoTitle = language === 'en' 
    ? 'Bulk Materials Delivery Texas | Commercial & Residential Projects'
    : 'Entrega de Materiales a Granel Texas | Proyectos Comerciales y Residenciales';
  
  const seoDescription = language === 'en'
    ? 'Bulk delivery of soil, rock, mulch, sand in Montgomery County TX. Commercial rates, volume pricing, multiple truck sizes. Professional delivery service.'
    : 'Entrega a granel de tierra, roca, mulch, arena en el Condado Montgomery TX. Tarifas comerciales, precios por volumen, múltiples tamaños de camión. Servicio de entrega profesional.';
  
  const seoKeywords = language === 'en'
    ? 'bulk delivery texas, bulk materials montgomery county, commercial landscaping delivery, volume pricing soil rock mulch sand'
    : 'entrega granel texas, materiales granel montgomery county, entrega paisajismo comercial, precios volumen tierra roca mulch arena';

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": seoTitle,
    "description": seoDescription,
    "provider": {
      "@type": "LocalBusiness",
      "name": "The Grass Depot LLC",
      "address": {
        "@type": "PostalAddress",
        "addressRegion": "TX",
        "addressLocality": "Montgomery County"
      }
    },
    "areaServed": "Montgomery County TX",
    "serviceType": "Bulk Materials Delivery"
  };

  const materials = [
    {
      name: language === 'en' ? 'Top Soil' : 'Tierra Vegetal',
      price: '$35',
      unit: language === 'en' ? 'per cubic yard' : 'por yarda cúbica',
      description: language === 'en' ? 'Premium screened topsoil' : 'Tierra vegetal tamizada premium',
      minOrder: language === 'en' ? '5 cubic yards' : '5 yardas cúbicas'
    },
    {
      name: language === 'en' ? 'River Rock' : 'Roca de Río',
      price: '$45',
      unit: language === 'en' ? 'per cubic yard' : 'por yarda cúbica',
      description: language === 'en' ? 'Natural river rock, multiple sizes' : 'Roca de río natural, múltiples tamaños',
      minOrder: language === 'en' ? '3 cubic yards' : '3 yardas cúbicas'
    },
    {
      name: language === 'en' ? 'Hardwood Mulch' : 'Mantillo de Madera Dura',
      price: '$30',
      unit: language === 'en' ? 'per cubic yard' : 'por yarda cúbica',
      description: language === 'en' ? 'Shredded hardwood mulch' : 'Mantillo de madera dura triturada',
      minOrder: language === 'en' ? '5 cubic yards' : '5 yardas cúbicas'
    },
    {
      name: language === 'en' ? 'Mason Sand' : 'Arena de Albañil',
      price: '$40',
      unit: language === 'en' ? 'per cubic yard' : 'por yarda cúbica',
      description: language === 'en' ? 'Fine grade construction sand' : 'Arena de construcción de grado fino',
      minOrder: language === 'en' ? '5 cubic yards' : '5 yardas cúbicas'
    }
  ];

  const clientTypes = [
    {
      type: language === 'en' ? 'Commercial Projects' : 'Proyectos Comerciales',
      icon: Building,
      features: language === 'en'
        ? ['Volume discounts', 'Contract pricing', 'Scheduled deliveries', 'Multiple locations']
        : ['Descuentos por volumen', 'Precios de contrato', 'Entregas programadas', 'Múltiples ubicaciones'],
      examples: language === 'en'
        ? ['Shopping centers', 'Office complexes', 'Apartment communities', 'Schools & parks']
        : ['Centros comerciales', 'Complejos de oficinas', 'Comunidades de apartamentos', 'Escuelas y parques']
    },
    {
      type: language === 'en' ? 'Residential Projects' : 'Proyectos Residenciales',
      icon: Home,
      features: language === 'en'
        ? ['Flexible scheduling', 'Placement service', 'Clean delivery', 'Expert advice']
        : ['Horarios flexibles', 'Servicio de colocación', 'Entrega limpia', 'Asesoramiento experto'],
      examples: language === 'en'
        ? ['Large landscaping', 'Pool backfill', 'Garden renovation', 'Driveway projects']
        : ['Paisajismo grande', 'Relleno de piscina', 'Renovación de jardín', 'Proyectos de entrada']
    }
  ];

  const deliveryProcess = [
    {
      step: '1',
      title: language === 'en' ? 'Quote Request' : 'Solicitud de Cotización',
      description: language === 'en' 
        ? 'Contact us with your material needs and quantity'
        : 'Contáctanos con tus necesidades de material y cantidad'
    },
    {
      step: '2',
      title: language === 'en' ? 'Site Assessment' : 'Evaluación del Sitio',
      description: language === 'en'
        ? 'We evaluate delivery access and placement options'
        : 'Evaluamos el acceso de entrega y opciones de colocación'
    },
    {
      step: '3',
      title: language === 'en' ? 'Scheduling' : 'Programación',
      description: language === 'en'
        ? 'Schedule delivery at your preferred time'
        : 'Programa la entrega en tu hora preferida'
    },
    {
      step: '4',
      title: language === 'en' ? 'Professional Delivery' : 'Entrega Profesional',
      description: language === 'en'
        ? 'Materials delivered and placed where you need them'
        : 'Materiales entregados y colocados donde los necesites'
    }
  ];

  const truckSizes = [
    {
      size: language === 'en' ? 'Single Axle' : 'Eje Sencillo',
      capacity: '10-12 ' + (language === 'en' ? 'cubic yards' : 'yardas cúbicas'),
      best: language === 'en' ? 'Residential driveways' : 'Entradas residenciales',
      weight: '26,000 lbs'
    },
    {
      size: language === 'en' ? 'Tandem Axle' : 'Eje Tándem',
      capacity: '15-18 ' + (language === 'en' ? 'cubic yards' : 'yardas cúbicas'),
      best: language === 'en' ? 'Large residential projects' : 'Proyectos residenciales grandes',
      weight: '52,000 lbs'
    },
    {
      size: language === 'en' ? 'Tri-Axle' : 'Tri-Eje',
      capacity: '20-25 ' + (language === 'en' ? 'cubic yards' : 'yardas cúbicas'),
      best: language === 'en' ? 'Commercial projects' : 'Proyectos comerciales',
      weight: '80,000 lbs'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <SEO 
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonical={language === 'en' ? '/en/services/bulk-delivery' : '/es/servicios/entrega-granel'}
        structuredData={structuredData}
        language={language}
        breadcrumbs={[
          { name: language === 'en' ? 'Home' : 'Inicio', url: `/${language}` },
          { name: language === 'en' ? 'Services' : 'Servicios', url: language === 'en' ? '/en/services' : '/es/servicios' },
          { name: language === 'en' ? 'Bulk Delivery' : 'Entrega a Granel', url: language === 'en' ? '/en/services/bulk-delivery' : '/es/servicios/entrega-granel' },
        ]}
      />
      <Header />
      
      <main>
      {/* Breadcrumb */}
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center text-sm text-muted-foreground">
          <Link to={`/${language}`} className="hover:text-primary transition-colors">
            {language === 'en' ? 'Home' : 'Inicio'}
          </Link>
          <ChevronRight className="h-4 w-4 mx-2" />
          <Link to={`/${language}/services`} className="hover:text-primary transition-colors">
            {language === 'en' ? 'Services' : 'Servicios'}
          </Link>
          <ChevronRight className="h-4 w-4 mx-2" />
          <span className="text-foreground">
            {language === 'en' ? 'Bulk Delivery' : 'Entrega a Granel'}
          </span>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative py-20 text-white overflow-hidden" style={{
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(${bulkDeliveryHero})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}>
        <div className="absolute inset-0 bg-primary/20"></div>
        <div className="container mx-auto px-6 relative">
          <div className="max-w-4xl animate-fade-in">
            <Link to={`/${language}/services`} className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors hover-scale">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {language === 'en' ? 'Back to Services' : 'Volver a Servicios'}
            </Link>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 animate-fade-in delay-200">
              {language === 'en' ? 'Bulk Materials Delivery' : 'Entrega de Materiales a Granel'}
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-8 animate-fade-in delay-400">
              {language === 'en' 
                ? 'Large quantity delivery for commercial and residential projects'
                : 'Entrega de grandes cantidades para proyectos comerciales y residenciales'
              }
            </p>
            <div className="flex flex-wrap gap-4 animate-fade-in delay-600">
              <Badge variant="secondary" className="bg-primary text-white border-primary px-4 py-2 text-base hover:bg-primary-dark transition-all hover-scale">
                <Package className="h-5 w-5 mr-2" />
                {language === 'en' ? 'Volume Pricing' : 'Precios por Volumen'}
              </Badge>
              <Badge variant="secondary" className="bg-primary-dark text-white border-primary-dark px-4 py-2 text-base hover:bg-primary transition-all hover-scale">
                <Clock className="h-5 w-5 mr-2" />
                {language === 'en' ? 'Reliable Scheduling' : 'Programación Confiable'}
              </Badge>
            </div>
          </div>
        </div>
      </section>

      {/* Materials Grid */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {language === 'en' ? 'Bulk Materials Available' : 'Materiales a Granel Disponibles'}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {language === 'en'
                ? 'High-quality materials available in bulk quantities with competitive pricing'
                : 'Materiales de alta calidad disponibles en cantidades a granel con precios competitivos'
              }
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {materials.map((material, index) => (
              <Card key={index} className="hover:shadow-glow transition-all duration-300 border-2 hover:border-primary">
                <CardHeader className="text-center">
                  <CardTitle className="text-lg font-bold text-foreground">
                    {material.name}
                  </CardTitle>
                  <div className="text-2xl font-bold text-primary">
                    {material.price}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {material.unit}
                  </div>
                </CardHeader>
                <CardContent className="text-center">
                  <p className="text-sm text-muted-foreground mb-4">
                    {material.description}
                  </p>
                  <div className="text-xs text-primary font-medium">
                    {language === 'en' ? 'Minimum order:' : 'Pedido mínimo:'} {material.minOrder}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Client Types */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {language === 'en' ? 'We Serve All Project Types' : 'Servimos Todos los Tipos de Proyecto'}
            </h2>
            <p className="text-lg text-muted-foreground">
              {language === 'en'
                ? 'Tailored solutions for commercial and residential needs'
                : 'Soluciones adaptadas para necesidades comerciales y residenciales'
              }
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {clientTypes.map((client, index) => {
              const IconComponent = client.icon;
              return (
                <Card key={index} className="hover:shadow-glow transition-all duration-300">
                  <CardHeader>
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mr-4">
                        <IconComponent className="h-6 w-6 text-primary" />
                      </div>
                      <CardTitle className="text-2xl font-bold text-foreground">
                        {client.type}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="mb-6">
                      <h4 className="font-semibold text-foreground mb-3">
                        {language === 'en' ? 'Services Include:' : 'Servicios Incluyen:'}
                      </h4>
                      <ul className="space-y-2">
                        {client.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center text-sm text-muted-foreground">
                            <CheckCircle className="h-4 w-4 text-primary mr-2 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground mb-3">
                        {language === 'en' ? 'Perfect For:' : 'Perfecto Para:'}
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {client.examples.map((example, idx) => (
                          <Badge key={idx} variant="secondary" className="text-xs">
                            {example}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>


      {/* Process Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {language === 'en' ? 'Delivery Process' : 'Proceso de Entrega'}
            </h2>
            <p className="text-lg text-muted-foreground">
              {language === 'en'
                ? 'Simple steps to get your bulk materials delivered'
                : 'Pasos simples para obtener tus materiales a granel entregados'
              }
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {deliveryProcess.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  {step.step}
                </div>
                <h3 className="text-xl font-bold text-foreground mb-2">{step.title}</h3>
                <p className="text-muted-foreground">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-secondary text-secondary-foreground">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            {language === 'en' 
              ? 'Ready for Bulk Delivery?' 
              : '¿Listo para Entrega a Granel?'
            }
          </h2>
          <p className="text-lg text-secondary-foreground/80 mb-8 max-w-2xl mx-auto">
            {language === 'en'
              ? 'Contact us for volume pricing and delivery scheduling. Our team will ensure your materials arrive when and where you need them.'
              : 'Contáctanos para precios por volumen y programación de entrega. Nuestro equipo se asegurará de que tus materiales lleguen cuándo y dónde los necesites.'
            }
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to={`/${language}/quote`}>
              <Button variant="hero" size="lg">
                <Calculator className="mr-2 h-5 w-5" />
                {language === 'en' ? 'Get Volume Quote' : 'Obtener Cotización por Volumen'}
              </Button>
            </Link>
            <a href="tel:9363200763">
              <Button variant="outline" size="lg" className="border-secondary-foreground/20 text-green-700 hover:bg-secondary-foreground/10">
                <Phone className="mr-2 h-5 w-5" />
                {language === 'en' ? 'Discuss Project' : 'Discutir Proyecto'}
              </Button>
            </a>
          </div>
        </div>
      </section>
      </main>

      <Footer />
    </div>
  );
};

export default BulkDeliveryPage;